import { CircleCheck } from "lucide-react";
import { toast } from "sonner";
import type {
  ClaimLineEdit,
  ClaimResponse,
  ClaimStrategyResponse,
} from "@patent-studio/api-types";
import { ConfirmButton } from "@/components/ui/ConfirmButton";
import {
  useAfterStageWrites,
  useConfirmClaim,
  useEditClaim,
  useUnconfirmClaim,
} from "@/hooks/use-claim-strategy";
import { Refusal } from "./ChainControls";
import { PLACEHOLDER_HELD } from "./ClaimOptions";
import { useClaimSetMoments } from "./claim-set-moments";
import { latestRefusal } from "./latest-refusal";

/**
 * The acts on one claim's words and standing: save the lines, and confirm or
 * withdraw the yes. Every write answers the
 * whole stage; the one refusal shown is the latest.
 *
 * A save of the lines is not busy. Leaving a line for Confirm starts one on
 * mousedown, and a control disabled before mouseup loses the click. Confirm
 * waits for that save instead, and reads the stage it answers, so the yes is
 * given to the words the attorney last wrote.
 */
export function useClaimActs(
  taskId: string,
  claimId: string,
  stage: ClaimStrategyResponse,
) {
  const edit = useEditClaim(taskId);
  const confirm = useConfirmClaim(taskId);
  const unconfirm = useUnconfirmClaim(taskId);
  const { refused, starting } = latestRefusal([edit, confirm, unconfirm]);
  const { after, waiting } = useAfterStageWrites(taskId);
  const { markTagging } = useClaimSetMoments();
  return {
    busy: waiting || confirm.isPending || unconfirm.isPending,
    refused,
    // Settles with the write, so the field that typed the lines can keep them when it fails.
    saveLines: async (lines: ClaimLineEdit[]) => {
      starting(edit);
      const served = await edit.mutateAsync({ claimId, body: { lines } });
      // Praxis tags the claim from its new words where the code could not.
      const saved = served.claims.find((c) => c.claim_id === claimId);
      if (served.tag_queued && saved !== undefined) {
        markTagging(claimId, saved.form_id);
      }
    },
    confirm: () => {
      after(stage, () => {
        starting(confirm, () => {
          confirm.mutate(claimId, {
            onSuccess: () => {
              toast("Claim confirmed.");
            },
          });
        });
      });
    },
    unconfirm: () => {
      starting(unconfirm, () => {
        unconfirm.mutate(claimId, {
          onSuccess: () => {
            toast("Confirmation removed.");
          },
        });
      });
    },
  };
}

type ClaimActs = ReturnType<typeof useClaimActs>;

/**
 * Confirm, and once confirmed the same control says so and withdraws the yes on
 * a press. A placeholder cannot be confirmed until it is cleared.
 */
export function ConfirmToggle({
  claim,
  acts,
}: {
  claim: ClaimResponse;
  acts: ClaimActs;
}) {
  if (claim.confirmed) {
    const when =
      claim.confirmed_at === null ? "" : ` ${claim.confirmed_at.slice(0, 10)}`;
    return (
      <ConfirmButton
        confirmed
        disabled={acts.busy}
        title={`Confirmed${when} · press to withdraw`}
        onClick={acts.unconfirm}
      >
        <CircleCheck aria-hidden />
        Confirmed
      </ConfirmButton>
    );
  }
  return (
    <ConfirmButton
      confirmed={false}
      disabled={acts.busy || claim.placeholder}
      title={claim.placeholder ? PLACEHOLDER_HELD : "Confirm this claim"}
      onClick={acts.confirm}
    >
      <CircleCheck aria-hidden />
      Confirm
    </ConfirmButton>
  );
}

/** A refused write on this claim, drawn as every other refusal on the screen is. */
export function ClaimRefusal({ acts }: { acts: ClaimActs }) {
  if (acts.refused === undefined) return null;
  return <Refusal error={acts.refused.error} />;
}
